import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { useCanEdit } from "@/lib/project-access";
import { toast } from "sonner";
import { RefreshCw, Eye, AlertCircle } from "lucide-react";
import type { GlossaryEntry, ProjectContext } from "@iamu/shared";
import {
  buildFullProjectContextText,
  buildLearnedPreferencesPrompt,
} from "@iamu/shared";
import { ProductIdentitySection } from "./product-identity-section";
import { GovernanceSection } from "./governance-section";
import { ActorsSection } from "./actors-section";
import { GlossarySection, formatGlossaryForPrompt } from "./glossary-section";
import { GuidanceSection } from "./guidance-section";
import { ArchitectureSection } from "./architecture-section";
import { PromptPreviewSheet } from "./prompt-preview-sheet";
import { ContextEmptyState } from "./context-empty-state";
import { getProvenanceLabel } from "./context-provenance";
import type { GuidanceRule } from "./guidance-rule-dialogs";

export interface ContextViewProps {
  projectId: string;
}

export function ContextView({ projectId }: ContextViewProps) {
  const {
    data: context,
    isLoading,
    isError,
    refetch,
  } = trpc.project.getContext.useQuery({ projectId });
  const { data: project } = trpc.project.getById.useQuery({ id: projectId });
  const { data: lastAnalysis } = trpc.project.lastAnalysis.useQuery({
    projectId,
  });
  const { data: rawGlossary } = trpc.terminology.getGlossary.useQuery({
    projectId,
  });
  const { data: guidanceRules, isLoading: isGuidanceLoading } =
    trpc.guidance.list.useQuery({ projectId, status: "all" });
  const utils = trpc.useUtils();
  const canEdit = useCanEdit();

  const [showPromptPreview, setShowPromptPreview] = useState(false);
  const [glossaryOverride, setGlossaryOverride] = useState<
    GlossaryEntry[] | null
  >(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isConfiguringManually, setIsConfiguringManually] = useState(
    searchParams.get("mode") === "manual",
  );

  const hasRepo = Boolean(
    project?.githubRepositoryId || project?.repositoryUrl,
  );

  function handleStartManualConfig() {
    setIsConfiguringManually(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("mode", "manual");
      return next;
    });
  }

  const updateProjectMutation = trpc.project.update.useMutation({
    onSuccess: (updated) => {
      utils.project.getById.setData({ id: projectId }, (prev) =>
        prev ? { ...prev, description: updated.description } : prev,
      );
      utils.project.getById.invalidate({ id: projectId });
    },
    onError: (err) => toastTrpcError(err, "Failed to update project"),
  });

  const updateContextMutation = trpc.project.updateContext.useMutation({
    onSuccess: (newContext) => {
      utils.project.getContext.setData({ projectId }, newContext);
    },
    onError: (err) => toastTrpcError(err, "Failed to update project context"),
  });

  function updateField(field: string, value: unknown) {
    updateContextMutation.mutate(
      { projectId, context: { [field]: value } },
      { onSuccess: () => toast.success("Project context updated") },
    );
  }

  function handleSaveIdentity({
    description,
    domain,
    industry,
  }: {
    description: string;
    domain?: string;
    industry?: string;
  }) {
    if (description !== (project?.description ?? "")) {
      updateProjectMutation.mutate({ id: projectId, description });
    }
    updateContextMutation.mutate(
      { projectId, context: { domain, industry, notes: description } },
      { onSuccess: () => toast.success("Product identity updated") },
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6" aria-busy="true">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32" />
        <Skeleton className="h-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
          <AlertCircle className="h-10 w-10 text-destructive" />
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">
              Failed to load project context
            </h2>
            <p className="max-w-md text-sm text-muted-foreground">
              We couldn&apos;t retrieve the context information for this
              project. Please try again.
            </p>
          </div>
          <Button variant="outline" className="gap-2" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const effectiveContext: ProjectContext = context ?? {};
  const projectGlossary = (project as { glossary?: unknown } | undefined)
    ?.glossary;
  const serverGlossary: GlossaryEntry[] = Array.isArray(rawGlossary)
    ? rawGlossary
    : Array.isArray(projectGlossary)
      ? (projectGlossary as GlossaryEntry[])
      : [];
  const effectiveGlossary = glossaryOverride ?? serverGlossary;

  const hasContextData =
    (!!context &&
      (!!context.domain ||
        !!context.industry ||
        !!(context.governedBy && context.governedBy.length > 0) ||
        !!(context.actors && context.actors.length > 0) ||
        !!context.architecture ||
        !!context.notes)) ||
    effectiveGlossary.length > 0;

  const activeGuidance = ((guidanceRules ?? []) as GuidanceRule[]).filter(
    (r) => r.status === "active",
  );

  const contextPrompt = buildFullProjectContextText({
    name: project?.name,
    description: project?.description ?? effectiveContext.notes ?? null,
    context: effectiveContext,
  });
  const glossaryPrompt = formatGlossaryForPrompt(effectiveGlossary);
  const guidancePrompt = buildLearnedPreferencesPrompt(
    activeGuidance.map((g) => ({
      summary: g.rule,
      reason: g.reason,
      kind: g.kind,
    })),
  );
  const promptText = [contextPrompt, glossaryPrompt, guidancePrompt]
    .filter(Boolean)
    .join("\n\n");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b pb-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>{getProvenanceLabel(project, lastAnalysis)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SimpleTooltip
            content={
              !hasContextData
                ? "Define domain context to preview the compiled runner prompt"
                : undefined
            }
          >
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPromptPreview(true)}
                disabled={!hasContextData}
              >
                <Eye className="mr-2 h-4 w-4 text-primary" />
                Preview runner prompt
              </Button>
            </div>
          </SimpleTooltip>
        </div>
      </div>

      {!hasContextData && !isConfiguringManually && (
        <ContextEmptyState
          projectId={projectId}
          hasRepo={hasRepo}
          canEdit={canEdit}
          onStartManualConfig={handleStartManualConfig}
        />
      )}

      {(hasContextData || isConfiguringManually) && (
        <div className="space-y-6">
          <ProductIdentitySection
            description={project?.description ?? (effectiveContext.notes || "")}
            domain={effectiveContext.domain ?? ""}
            industry={effectiveContext.industry ?? ""}
            onSave={handleSaveIdentity}
            canEdit={canEdit}
            isPending={
              updateProjectMutation.isPending || updateContextMutation.isPending
            }
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            <GovernanceSection
              context={effectiveContext}
              onUpdate={updateField}
              canEdit={canEdit}
            />
            <ActorsSection
              context={effectiveContext}
              onUpdate={updateField}
              canEdit={canEdit}
            />
          </div>

          <GlossarySection
            projectId={projectId}
            glossary={effectiveGlossary}
            canEdit={canEdit}
            onGlossaryChange={setGlossaryOverride}
          />

          <GuidanceSection
            projectId={projectId}
            rules={guidanceRules as GuidanceRule[] | undefined}
            isLoading={isGuidanceLoading}
          />

          <ArchitectureSection
            context={effectiveContext}
            onUpdate={updateField}
            canEdit={canEdit}
          />
        </div>
      )}

      <PromptPreviewSheet
        open={showPromptPreview}
        onOpenChange={setShowPromptPreview}
        promptText={promptText ?? ""}
        contextPrompt={contextPrompt ?? ""}
        glossaryPrompt={glossaryPrompt}
        guidancePrompt={guidancePrompt ?? ""}
        projectName={project?.name ?? "Project"}
      />
    </div>
  );
}
